import type { fetchFeaturesFamilyMembersOrThrow } from "../../api/fetch.ts";
import type { MiniFamily } from "../../synteny-worker/state.ts";
import type { TrackGroups, Tracks, TrackType } from "../../types.ts";
import type { MemberItem } from "../align.ts";

export function buildFamilyFromFamilyData(
  familyMembers: Awaited<ReturnType<typeof fetchFeaturesFamilyMembersOrThrow>>,
  fromMemberItem: MemberItem,
  tracks: Tracks,
  trackGroups: TrackGroups,
  trackType?: TrackType,
  existingFamily?: MiniFamily,
): MiniFamily {
  const newFamily: MiniFamily = {
    familyId: fromMemberItem.familyId,
    members: [],
  };

  const organismContigToTrackGroups = new Map<string, string[]>();
  const trackGroupToTrackId = new Map<string, string>();

  for (const [groupId, trackGroup] of trackGroups) {
    const track = Array.from(tracks.values()).find(
      (track) => track.groupId === groupId && track.trackType === trackType,
    );

    if (!track) continue;

    trackGroupToTrackId.set(groupId, track.trackId);

    const key = `${trackGroup.organismId}:${trackGroup.contigId}`;
    if (!organismContigToTrackGroups.has(key)) {
      organismContigToTrackGroups.set(key, []);
    }
    organismContigToTrackGroups.get(key)!.push(groupId);
  }

  const existingMembersMap = new Map<string, number>();
  if (existingFamily?.members) {
    for (const member of existingFamily.members) {
      existingMembersMap.set(member.trackId, member.featureId);
    }
  }

  for (const organism of familyMembers) {
    for (const contig of organism.contigs) {
      if (contig.members.length === 0) continue;

      const key = `${organism.organismId}:${contig.contigId}`;
      const trackGroupsForContig = organismContigToTrackGroups.get(key) || [];

      const sortedMembers = [...contig.members].sort((a, b) => a.start - b.start);

      for (let i = 0; i < trackGroupsForContig.length; i++) {
        const groupId = trackGroupsForContig[i];
        const trackId = trackGroupToTrackId.get(groupId);

        if (!trackId) continue;

        let assignedMemberId: number;

        if (trackId === fromMemberItem.trackId) {
          assignedMemberId = fromMemberItem.featureId;
        } else if (existingMembersMap.has(trackId)) {
          assignedMemberId = existingMembersMap.get(trackId)!;
        } else if (i < sortedMembers.length) {
          assignedMemberId = sortedMembers[i].locationId;
        } else {
          assignedMemberId = sortedMembers[0].locationId;
        }

        newFamily.members.push({
          trackId,
          featureId: assignedMemberId,
        });
      }
    }
  }

  return newFamily;
}
